import { core } from '@servicetitan/anvil2/token';
import { PreApprovalStatus } from '@servicetitan/financing-api-client/src/prequals-api';

// Kept exhaustive over PreApprovalStatus even though the Prescreening tab only ever
// lists Approved rows today, so widening the status filter later is a one-line change.
const PRE_APPROVAL_STATUS_TO_DISPLAY: Record<PreApprovalStatus, string> = {
    [PreApprovalStatus.Unknown]: 'Unknown',
    [PreApprovalStatus.Submitted]: 'Submitted',
    [PreApprovalStatus.Approved]: 'Pre-Approved',
    [PreApprovalStatus.Declined]: 'Declined',
    [PreApprovalStatus.Indeterminate]: 'Indeterminate',
    [PreApprovalStatus.MovedToLoanApplication]: 'Moved to Loan Application',
};

// Expiry isn't a PreApprovalStatus the backend returns (an expired row keeps
// Status = Approved forever) -- the MFE computes it client-side from ExpirationDate
// and stores this sentinel in place of the raw status so the grid can display,
// filter, and sort on it like any other status value.
export const PRE_APPROVAL_EXPIRED_STATUS = 'expired';

export const getPreApprovalStatusDisplayText = (status: PreApprovalStatus | string): string => {
    if (status === PRE_APPROVAL_EXPIRED_STATUS) {
        return 'Expired';
    }
    return PRE_APPROVAL_STATUS_TO_DISPLAY[status as PreApprovalStatus] ?? String(status);
};

export const getPreApprovalStatusColor = (status: PreApprovalStatus | string): string => {
    if (status === PRE_APPROVAL_EXPIRED_STATUS) {
        return core.primitive.ColorNeutral200.value;
    }
    switch (status) {
        case PreApprovalStatus.Approved:
            return core.primitive.ColorGreen50.value;
        case PreApprovalStatus.Declined:
            return core.primitive.ColorRed50.value;
        case PreApprovalStatus.Submitted:
        case PreApprovalStatus.Indeterminate:
            return core.primitive.ColorYellow200.value;
        case PreApprovalStatus.Unknown:
        case PreApprovalStatus.MovedToLoanApplication:
        default:
            return core.primitive.ColorNeutral200.value;
    }
};

// Check if a pre-approval status matches any of the selected filter statuses by display text
export const preApprovalStatusMatchesFilter = (
    status: PreApprovalStatus | string,
    selectedStatuses: (PreApprovalStatus | string)[],
): boolean => {
    const displayText = getPreApprovalStatusDisplayText(status);
    return selectedStatuses.some(
        selectedStatus => getPreApprovalStatusDisplayText(selectedStatus) === displayText,
    );
};
